import { useCallback, useEffect, useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Sparkles } from "lucide-react";
import {
  buyerResearchApi,
  type BuyerResearchBriefDto,
  type BuyerResearchConversationEntry,
  type BuyerResearchConversationResume,
} from "@/api/buyerResearch";
import { extractErrorMessage } from "@/api/client";
import { Button, Textarea } from "@/design-system/components/ui";
import { useDateFormatter } from "@/utils/datetime";
import { researchCopy } from "./researchCopy";
import type { ResearchCounting } from "./useResearchCount";
import type { ResearchConversationState } from "./useResearchConversationState";

/**
 * Conversational search — building the query with the specialist against
 * Clef's FREE count endpoint, before anything is bought.
 *
 * <p>🔴 <b>THE CONVERSATION EDITS THE BRIEF, NEVER A FILTER (RE3).</b> Every
 * shape that crosses the wire here carries a whole
 * {@link BuyerResearchBriefDto}; the server re-resolves the industry codes and
 * derives the vendor filter exactly as a run does. A filter-shaped conversation
 * could not move the industry at all — the shipped Ask Phoenix classifier says
 * so in as many words — so "just plumbing, not all trades" would degrade to a
 * text match over rows already bought.</p>
 *
 * <p>🔴 <b>NOTHING IS PERSISTED AGAINST A RUN (RE2).</b> The transcript and the
 * working brief live in the CLIENT — the transcript one level up, in
 * {@link ResearchConversationState}, since the stepped brief unmounts this
 * component on a step change — and are sent whole on every turn. The
 * one thing the server keeps is a per-user RESUME row, written at the end of
 * each turn and read back by the brief endpoint when the drawer opens.</p>
 *
 * <p>🔴 <b>THE COUNT IS NOT OWNED HERE (WS3).</b> It lives in
 * {@link useResearchCount}, which the OWNER calls once and passes down, because
 * a second surface now reads the same number. It used to be held here and
 * mirrored up through an `onCountChange` callback — one number in two places,
 * the second only as fresh as the last callback.</p>
 *
 * <p>🔴 <b>RE6 — THE COUNT ARRIVES A BEAT LATER AND NEVER GATES THE RENDER.</b>
 * The drawer opens on the brief; the first count is fired here on mount and
 * lands behind it. A surface that waited for a number before showing anything
 * would re-create the sluggishness this whole feature exists to remove, which
 * is why {@link ResearchConversation} renders its opening line and its
 * composer with `count` still null and why no caller may `await` it.</p>
 *
 * <p>🔴 <b>THE COUNT IS MARKED STALE RATHER THAN RE-TAKEN ON EVERY KEYSTROKE.</b>
 * A count describes the query it was taken against, so a hand-edit in the
 * fields below leaves the number on screen describing a different search —
 * which is the exact symptom this feature exists to dissolve. Re-counting on
 * every change would be a vendor request per character, so the working brief is
 * compared against the one that was counted and the number is struck through
 * with an offer to take a fresh one.</p>
 */
export function ResearchConversation({
  buyerLeadId,
  brief,
  counting,
  state,
  initialResume,
  onApplyBrief,
  onSettledChange,
  canEdit,
}: {
  buyerLeadId: string;
  /**
   * The working brief as the fields below currently hold it, or null while a
   * money field refuses to parse — there is nothing to count then, and a count
   * of a half-typed query would be worse than none.
   */
  brief: BuyerResearchBriefDto | null;
  /**
   * The shared count, owned above this component so the stepped brief and the
   * run control read the SAME number rather than a mirror of it. Seeded with
   * the guards the brief endpoint read; superseded by the guards every count
   * and every turn carries, which describe whatever was last measured.
   */
  counting: ResearchCounting;
  /**
   * 🔴 The transcript, the composer's unsent text and any unanswered route
   * offer, owned ABOVE this component for the same reason `counting` is: the
   * stepped brief renders one step at a time, so this component unmounts the
   * moment a specialist steps off it, and component state would take the
   * conversation with it. See {@link ResearchConversationState}.
   */
  state: ResearchConversationState;
  initialResume: BuyerResearchConversationResume | null;
  /** 🔴 The client applies a proposal; the server persists nothing. */
  onApplyBrief: (brief: BuyerResearchBriefDto) => void;
  /**
   * 🔴 WS4's carrier: a specialist has taken at least one turn, so the adaptive
   * probe must not narrow the query they settled. Reported up rather than
   * decided at the run, because THIS is the component that knows.
   */
  onSettledChange: (settled: boolean) => void;
  canEdit: boolean;
}) {
  const copy = researchCopy.conversation;
  const { dateTime } = useDateFormatter();

  const { count, guards } = counting;

  const { entries, setEntries, append, instruction, setInstruction, route, setRoute, kept, setKept, seeded } =
    state;

  // Local on purpose: an error describes the LAST ATTEMPT, and an attempt is
  // over by the time somebody has walked to another step and back. Carrying it
  // across would re-raise an alert about something already moved past.
  const [turnError, setTurnError] = useState<string | null>(null);

  const briefJson = brief ? JSON.stringify(brief) : null;

  const note = useCallback(
    (text: string) => append({ role: "note", text, atUtc: new Date().toISOString(), counts: null, kind: null }),
    [append],
  );

  const turnMutation = useMutation({
    // no-invalidation: a turn persists nothing any cached read serves. The one
    // row it writes is the per-user RESUME blob, and that is read only by the
    // brief endpoint on the next open of this drawer — which is a fresh
    // mutation, not a query this client holds. The transcript and the working
    // brief are component state by design (RE2).
    mutationFn: (v: {
      brief: BuyerResearchBriefDto;
      transcript: BuyerResearchConversationEntry[];
      instruction: string;
    }) => buyerResearchApi.converse(buyerLeadId, v.brief, v.transcript, v.instruction),
  });

  /**
   * Seeded ONCE per open, behind a ref.
   *
   * <p>🔴 The effect must not re-run when the brief moves: the drawer's own
   * fields write to it on every keystroke, and a re-seed there would replace a
   * live transcript with a parked one mid-sentence. The ref is the same
   * once-per-open idiom the offer wizard uses for exactly this hazard.</p>
   */
  useEffect(() => {
    if (seeded.current || !brief) return;
    seeded.current = true;

    const resumed = initialResume?.transcript ?? [];
    if (resumed.length > 0) {
      setEntries(resumed);
      note(
        initialResume?.truncated
          ? copy.resumedTruncated(initialResume.droppedTurns)
          : copy.resumed,
      );
      onSettledChange(resumed.some((e) => e.role === "specialist"));
    }

    counting.take(brief);
  }, [brief, copy, counting, initialResume, note, onSettledChange, seeded, setEntries]);

  /**
   * 🔴 THE SAME FACT THE SERVER READS TO SUPPRESS THE PROBE, derived from the
   * same evidence the parent is told about: a transcript carrying a specialist
   * turn. `onSettledChange` pushes exactly this up at both of its call sites,
   * so the caption below and the parent's run button cannot disagree about
   * whether the count is a promise.
   */
  const settled = entries.some((e) => e.role === "specialist");

  const send = () => {
    const text = instruction.trim();
    if (!text || !brief) return;
    setTurnError(null);
    setRoute(null);

    const outbound: BuyerResearchConversationEntry = {
      role: "specialist",
      text,
      atUtc: new Date().toISOString(),
      counts: null,
      kind: null,
    };
    // Their own words need no server to be true, so they go up immediately and
    // the box empties — a composer that holds a sentence hostage to a round
    // trip reads as a dropped message.
    const payloadTranscript = [...entries, outbound];
    setEntries(payloadTranscript);
    setInstruction("");
    onSettledChange(true);

    turnMutation.mutate(
      { brief, transcript: entries, instruction: text },
      {
        onSuccess: (turn) => {
          counting.adoptGuards(turn.guards);
          append({
            role: "phoenix",
            text: turn.reply,
            atUtc: new Date().toISOString(),
            counts: turn.counts.length > 0 ? turn.counts : null,
            kind: turn.kind,
          });

          if (turn.unmeasured.length > 0) note(copy.unmeasured(turn.unmeasured));

          // 🔴 The proposal is APPLIED, not offered: the specialist asked for
          // it in words, and a second confirmation on every turn is the
          // friction the conversation exists to remove. The route below is the
          // one thing that IS a question, because the server asked one.
          const applied = turn.proposedBrief;
          if (applied) onApplyBrief(applied);

          // The turn's own current count describes the brief the turn
          // proposed, so it is adopted without a second vendor call. When the
          // turn spent its budget elsewhere (RE4) there is none, and the number
          // on screen is marked stale rather than left to speak for a query it
          // does not describe.
          const current = turn.counts.find((c) => c.isCurrent) ?? null;
          counting.adopt(current, current ? JSON.stringify(applied ?? brief) : null);

          if (turn.route) setRoute(turn.route);
        },
        onError: (e: unknown) => {
          setTurnError(copy.turnFailed(extractErrorMessage(e)));
          // Put the instruction back rather than making them retype it.
          setInstruction(text);
          setEntries(entries);
        },
      },
    );
  };

  /**
   * Ruling 1 — the specialist's answer to the route the server offered.
   *
   * <p>🔴 <b>ACCEPTING APPENDS THE CLAUSE TO THE BRIEF'S OWN `notes`, which is
   * the SAME channel Ask Phoenix's re-rank already uses</b>
   * (`BuildRefinementScores` appends its instruction to `brief.Notes`, and says
   * so in place). So the clause reaches the ranking prompt as a stated
   * preference — a real effect on the order of the list — and it lands on the
   * run's own record, where the read-only brief renders it beside the results.
   * It is NOT a hard vendor filter and no copy here says it is: the free
   * in-place filter is the post-run Ask Phoenix action the server's own route
   * sentence offers, and the standing line says exactly that.</p>
   *
   * <p>Both answers apply the Layer A narrowing, because both were asked for —
   * the route's own sentence is "I'll narrow the buy … then filter these
   * results … Want me to?", so declining declines the SECOND half.</p>
   */
  const answerRoute = (accept: boolean) => {
    if (!route) return;
    if (accept) {
      setKept((prev) => [...prev, route.clause]);
      note(copy.routeAccepted(route.clause));
      if (brief) {
        const notes = brief.notes?.trim()
          ? `${brief.notes.trim()}\n\n${route.clause}`
          : route.clause;
        onApplyBrief({ ...brief, notes });
      }
    } else {
      note(copy.routeDeclined(route.clause));
    }
    setRoute(null);
  };

  const stale = counting.isStaleFor(briefJson);

  const runsLeft = guards ? guards.runsAllowedInWindow - guards.runsUsedInWindow : null;

  return (
    <section className="grid gap-space-3" data-testid="research-conversation">
      <h3 className="m-0 text-caption uppercase tracking-wide text-gray-mid">{copy.heading}</h3>

      {/* RE6 — this line is what stands in for the count while it is still in
          flight, so the drawer always has something true to say. */}
      {count == null && !counting.error && (
        <p className="m-0 text-small text-gray-mid" data-testid="research-conversation-opening">
          {counting.isCounting ? copy.briefReady : copy.opening}
        </p>
      )}

      <ol className="m-0 p-0 list-none grid gap-space-2" data-testid="research-conversation-transcript">
        {entries.map((e, i) => (
          <li
            key={`${i}-${e.atUtc}`}
            className={
              e.role === "note"
                ? "text-caption text-gray-mid break-words"
                : "rounded-md border border-border-light bg-white-pure px-space-3 py-space-2 grid gap-space-1"
            }
            data-testid="research-conversation-entry"
            data-role={e.role}
            data-kind={e.kind ?? undefined}
          >
            {e.role === "note" ? (
              e.text
            ) : (
              <>
                <span className="text-caption uppercase tracking-wide text-gray-mid">
                  {e.role === "specialist" ? copy.speakerYou : copy.speakerPhoenix}
                </span>
                <span className="text-small text-onyx break-words whitespace-pre-wrap">{e.text}</span>
                {(e.counts ?? []).length > 0 && (
                  <ul className="m-0 p-0 list-none grid gap-[2px]">
                    {(e.counts ?? []).map((c, ci) => (
                      <li
                        key={`${ci}-${c.label}`}
                        className="text-caption text-gray-mid tabular-nums break-words"
                        data-testid="research-conversation-count"
                      >
                        {copy.matchesFor(c.matchCount, c.label)}
                        {c.approximate && ` — ${copy.approximate}`}
                      </li>
                    ))}
                  </ul>
                )}
              </>
            )}
          </li>
        ))}
      </ol>

      {/* Ruling 1 — the ROUTE. The sentence is the SERVER'S, rendered verbatim:
          it is composed in one place precisely so it names both halves every
          time, and re-composing it here would be free to drop one and still
          read well. A shrug is a failed turn; this is the alternative. */}
      {route && (
        <div
          className="grid gap-space-2 rounded-md border border-gold bg-white-warm px-space-3 py-space-2"
          data-testid="research-conversation-route"
          data-route-field={route.field}
        >
          <p className="m-0 text-small text-onyx break-words">
            <Sparkles size={12} aria-hidden /> {route.sentence}
          </p>
          {canEdit && (
            <div className="flex flex-wrap gap-space-2">
              <Button
                size="sm"
                onClick={() => answerRoute(true)}
                data-testid="research-conversation-route-accept"
              >
                {copy.routeAccept}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => answerRoute(false)}
                data-testid="research-conversation-route-decline"
              >
                {copy.routeDecline}
              </Button>
            </div>
          )}
        </div>
      )}

      {kept.length > 0 && (
        <ul className="m-0 p-0 list-none grid gap-[2px]" data-testid="research-conversation-kept">
          {kept.map((clause, i) => (
            <li key={`${i}-${clause}`} className="text-caption text-gray-mid break-words">
              {copy.routePending(clause)}
            </li>
          ))}
        </ul>
      )}

      {/* The count, and exactly how far its promise reaches.

          The number came from the free count against the filter this EXACT
          brief derives — same `BuildFilter`, so it is the same query. 🔴 That
          reason is sound and the conclusion "this is what the buy will honour"
          only follows once the specialist has SETTLED the query: on a run with
          no specialist turn the server still converges a broad query before
          buying, re-counts against the narrowed filter, and buys that. So the
          figure is stated plainly, and the caption beneath it says whether it
          is the number the purchase will honour — rather than leaving the
          number to imply a promise Phoenix has not made. */}
      {count != null && (
        <p
          className="m-0 text-small text-onyx break-words"
          data-testid="research-conversation-current-count"
          data-stale={stale || undefined}
        >
          {count.matchCount === 0 ? (
            <span className="text-gray-mid">{copy.noMatches}</span>
          ) : (
            <>
              <span className={stale ? "text-gray-mid line-through" : "tabular-nums"}>
                {copy.matches(count.matchCount)}
              </span>
              {count.approximate && (
                <span className="text-caption text-gray-mid"> — {copy.approximate}</span>
              )}
            </>
          )}
        </p>
      )}

      {/* 🔴 WHOSE NUMBER IS IT — asked here because the specialist is about to
          spend ~100 records on the answer.

          Rendered only where all four hold: there is a count, it found
          something (nothing narrows a zero, and `noMatches` already says so),
          the specialist has not settled the query, and the count is not already
          struck through (the stale notice below is a better sentence than this
          one, and stacking them reads as two problems). */}
      {count != null && count.matchCount > 0 && !settled && !stale && (
        <p
          className="m-0 text-caption text-gray-mid break-words"
          data-testid="research-conversation-may-narrow"
        >
          {copy.mayNarrowBeforeBuying}
        </p>
      )}

      {stale && (
        <div className="flex flex-wrap items-center gap-space-2" data-testid="research-conversation-stale">
          <span className="text-caption text-gray-mid break-words">{copy.countStale}</span>
          <Button
            size="sm"
            variant="secondary"
            loading={counting.isCounting}
            onClick={() => brief && counting.take(brief)}
            data-testid="research-conversation-count-again"
          >
            {counting.isCounting ? copy.counting : copy.countAgain}
          </Button>
        </div>
      )}

      {counting.error && (
        <p className="m-0 text-caption text-status-error break-words" role="alert" data-testid="research-conversation-count-error">
          {counting.error}
        </p>
      )}

      {/* RE8 — both guards, on screen while building rather than at RUN. A
          specialist who converses for five minutes and is then refused has been
          failed, and the re-run guard is the sharper of the two because it only
          trips once they have settled on a query. */}
      {guards && (
        <div className="grid gap-[2px]" data-testid="research-conversation-guards">
          <span className="text-caption text-gray-mid break-words">
            {runsLeft != null && runsLeft > 0
              ? copy.runsLeft(guards.runsUsedInWindow, guards.runsAllowedInWindow)
              : copy.runsNoneLeft(
                  guards.nextRunAllowedAtUtc ? dateTime(guards.nextRunAllowedAtUtc) : null,
                )}
          </span>
          {guards.reRunGuardWouldRefuse && (
            <span
              className="text-caption text-status-error break-words"
              data-testid="research-conversation-rerun-guard"
            >
              {copy.reRunWouldSkipClef(
                guards.identicalRunAtUtc ? dateTime(guards.identicalRunAtUtc) : "",
                guards.reRunWindowHours,
              )}
            </span>
          )}
        </div>
      )}

      {canEdit && (
        <div className="grid gap-space-2">
          <Textarea
            rows={2}
            value={instruction}
            onChange={(e) => setInstruction(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={copy.placeholder}
            aria-label={copy.heading}
            disabled={turnMutation.isPending}
            data-testid="research-conversation-input"
          />
          <div className="flex flex-wrap items-center gap-space-2">
            <Button
              size="sm"
              loading={turnMutation.isPending}
              disabled={!instruction.trim() || !brief || turnMutation.isPending}
              onClick={send}
              data-testid="research-conversation-send"
            >
              {turnMutation.isPending ? copy.sendPending : copy.send}
            </Button>
            {!brief && (
              <span className="text-caption text-gray-mid" data-testid="research-conversation-invalid">
                {copy.briefInvalid}
              </span>
            )}
          </div>
        </div>
      )}

      {turnError && (
        <p className="m-0 text-caption text-status-error break-words" role="alert" data-testid="research-conversation-error">
          {turnError}
        </p>
      )}
    </section>
  );
}
